import { Button, Card, Descriptions, Modal, message, Spin, Table, Collapse } from "antd";
import React, { Fragment } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import EntitledSection from "../components/EntitledSection";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import entitlements from "../config/entitlements";
import { Quarter, AssetAllocation, EconomicOutlook } from "../helpers/shared-interfaces";
import { request } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import { DELETE_QUARTER, GET_QUARTER } from "../gql/economicIndicators";
import moment from "moment";

interface RouteParams {
  id: string;
}

interface State {
  name: string;
  isLoading: boolean;
  id: string | number;
  showDialog: boolean;
  quarter?: Quarter;
  assetAllocations?: AssetAllocation[];
  economicOutlooks?: EconomicOutlook[];
}

export const getAssetAllocationColumns = [
  {
    title: "Asset",
    dataIndex: "asset",
    key: "asset",
    render(text: string, record: AssetAllocation): JSX.Element {
      return <span>{record?.assetAllocationItem?.name}</span>;
    },
  },
  {
    title: "Rate Type",
    dataIndex: "rateType",
    key: "rateType",
    render(text: string, record: AssetAllocation): JSX.Element {
      return <span>{record?.assetAllocationRate?.name}</span>;
    },
  },
  {
    title: "Scheme",
    dataIndex: "scheme",
    key: "scheme",
    render(text: string, record: AssetAllocation): JSX.Element {
      return <span>{record?.product?.name}</span>;
    },
  },
  {
    title: "Enable",
    dataIndex: "enable",
    key: "enable",
    render(text: string, record: AssetAllocation): JSX.Element {
      return <span>{record?.enable?.toString()}</span>;
    },
  },
  {
    title: "Rate %",
    dataIndex: "rate%",
    key: "rate%",
    render(text: string, record: AssetAllocation): JSX.Element {
      return <span>{record?.rate}</span>;
    },
  },
  {
    title: "Order",
    dataIndex: "order",
    key: "order",
    render(text: string, record: AssetAllocation): JSX.Element {
      return <span>{record?.order}</span>;
    },
  },
];

export const getOutlookColumns = [
  {
    title: "Outlook",
    dataIndex: "outlook",
    key: "outlook",
    render(text: string, record: EconomicOutlook): JSX.Element {
      return <span>{record.outlook}</span>;
    },
  },
  {
    title: "Created At",
    dataIndex: "createdAt",
    key: "createdAt",
    render(text: string, record: EconomicOutlook): JSX.Element {
      return <span>{moment(record.createdAt).format('DD/MM/YYYY HH:mm')}</span>;
    },
  },
];

function ModalWrapper(
  showDialog: boolean,
  deleteQuarter: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteQuarter}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this quarter?</div>
    </Modal>
  );
}

export default class ViewQuarter extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      name: "",
      isLoading: true,
      id: "",
      showDialog: false,
      quarter: undefined,
      assetAllocations: [],
      economicOutlooks: [],
    };
    this.deleteQuarter = this.deleteQuarter.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getQuarter();
    });
  }

  getQuarter = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_QUARTER,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            this.setState({
              quarter: data.data.quarter,
              assetAllocations: data.data.quarter?.assetAllocations,
              economicOutlooks: data.data.quarter?.economicOutlooks || [],
            });
          } else {
            message.error("Failed to get quarter data");
          }
        });
      },
    );
  };

  deleteQuarter(): void {
    const { id } = this.state;

    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_QUARTER,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false, showDialog: false });
            if (!data.errors && data.data?.deleteQuarter?.result) {
              message.success("Quarter deleted successfuly");
              this.redirectToAllQuartersPage();
            } else {
              message.error("Failed to delete quarter");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false, showDialog: false });
          });
      },
    );
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllQuartersPage(): void {
    this.props.history.push("/quarters");
  }
  render(): JSX.Element | null {
    const { quarter, isLoading, id, showDialog, assetAllocations, economicOutlooks } = this.state;
    const year = quarter?.startDate.split("-")[0];
    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              {ModalWrapper(
                showDialog,
                this.deleteQuarter,
                this.toggleDeleteConfirmationModal,
              )}
              <Descriptions
                title={<div>{`${quarter?.name}-${year}`}</div>}
                extra={
                  <div>
                    <EntitledSection
                      entitlement={entitlements.EconomicIndicators.edit}
                    >
                      <NavLink to={`/quarters/${id}/edit`}>
                        <Button
                          icon={<EditOutlined />}
                          size="large"
                          style={{
                            backgroundColor: "  rgb(29, 55, 100)",
                            color: "white",
                            marginRight: "10px",
                          }}
                          data-cy="test-quarter-edit-button"
                        >
                          Edit
                        </Button>
                      </NavLink>
                    </EntitledSection>
                    <EntitledSection
                      entitlement={entitlements.EconomicIndicators.delete}
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={this.toggleDeleteConfirmationModal}
                        data-cy="test-quarter-delete-button"
                      >
                        Delete
                      </Button>
                    </EntitledSection>
                  </div>
                }
              >
                <Descriptions.Item label={<b>Quarter</b>} key="Name">
                  {quarter?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Start Date</b>} key="Department">
                  {quarter?.startDate}
                </Descriptions.Item>
                <Descriptions.Item label={<b>End Date</b>} key="Is Activeame">
                  {quarter?.endDate}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Cedi Depreciation</b>} key="CediDepreciation">
                  {quarter?.cediDepreciation ? `${quarter.cediDepreciation}%` : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={<b>GDP</b>} key="GDP">
                  {quarter?.gdp ? `${quarter.gdp}%` : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={<b>Inflation</b>} key="Inflation">
                  {quarter?.inflation ? `${quarter.inflation}%` : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={<b>T-Bill 91</b>} key="TBill91">
                  {quarter?.tbill91 ? `${quarter.tbill91}%` : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={<b>T-Bill 182</b>} key="TBill182">
                  {quarter?.tbill182 ? `${quarter.tbill182}%` : "-"}
                </Descriptions.Item>
                <Descriptions.Item label={<b>T-Bill 365</b>} key="TBill365">
                  {quarter?.tbill365 ? `${quarter.tbill365}%` : "-"}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div>
              <Collapse
                style={{
                  marginBottom: "12px",
                }}
              >
                <Collapse.Panel key="assetAllocations" header="Asset Allocations">
                  {assetAllocations && (
                    <Table
                      scroll={{ x: 400 }}
                      columns={getAssetAllocationColumns}
                      dataSource={assetAllocations}
                    />
                  )}
                </Collapse.Panel>
              </Collapse>
            </div>
            <div>
              <Collapse
                style={{
                  marginBottom: "12px",
                }}
              >
                <Collapse.Panel key="outlooks" header="Outlooks">
                  {economicOutlooks && (
                    <Table
                      scroll={{ x: 400 }}
                      columns={getOutlookColumns}
                      dataSource={economicOutlooks}
                      rowKey="id"
                    />
                  )}
                </Collapse.Panel>
              </Collapse>
            </div>
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
